import { ChevronDown, ChevronRight, Plus, Tags, TriangleAlert } from 'lucide-react';
import { useState } from 'react';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { FilterPills } from '@/components/ui/filter-pills';
import { Pagination } from '@/components/ui/pagination';
import { SearchInput } from '@/components/ui/search-input';
import { StatusBadge } from '@/components/ui/status-badge';
import { CategoryFormDialog } from '@/pages/admin/property-categories/CategoryFormDialog';
import { ChecklistEditor } from '@/pages/admin/property-categories/ChecklistEditor';
import { usePropertyCategories } from '@/hooks/property-categories';
import { ACTIVITY_FILTER_OPTIONS, DEFAULT_PAGE_SIZE } from '@shared/schemas/pagination';
import type { ActivityFilter } from '@shared/schemas/pagination';
import type { PropertyCategoryView } from '@shared/schemas/catalog';

/*
 * /admin/property-categories — ROADMAP.md §3.2.
 *
 * A list of property types, each expanding to the checklist it puts on a job.
 * The expand-to-see-tasks shape is the prototype's and is kept: the checklist is
 * the thing people come here to change, and putting it one click away rather
 * than one page away is why the prototype's version works.
 *
 * The rows are cards rather than `DataTable`, because a table row cannot open.
 * Loading, empty and error are therefore handled here by hand — all three, which
 * is the rule most often skipped (ROADMAP.md §4).
 */

export function PropertyCategoriesPage() {
  const [search, setSearch] = useState('');
  const [activity, setActivity] = useState<ActivityFilter>('active');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);

  const categories = usePropertyCategories({ page, search, activity });

  function changeFilter(next: ActivityFilter) {
    setActivity(next);
    // Page 3 of the active list is rarely page 3 of the archived one, and
    // landing on an empty page reads as "there is nothing here".
    setPage(1);
  }

  return (
    <AdminPage
      title="Property Categories"
      description="The property types, and the task checklist each one puts on a job."
      actions={
        <Button
          onClick={() => {
            setCreating(true);
          }}
        >
          <Plus aria-hidden="true" />
          Add category
        </Button>
      }
    >
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          label="Search property categories"
          placeholder="Search categories"
          className="sm:max-w-xs"
        />
        <FilterPills
          label="Filter categories by status"
          options={ACTIVITY_FILTER_OPTIONS}
          value={activity}
          onChange={changeFilter}
        />
      </div>

      {categories.isError ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            onRetry={() => {
              void categories.refetch();
            }}
          />
        </div>
      ) : categories.isPending ? (
        <ul aria-busy="true" className="space-y-3">
          <li className="sr-only">Loading property categories</li>
          {Array.from({ length: 4 }, (_, index) => (
            <li
              key={index}
              aria-hidden="true"
              className="h-[72px] animate-pulse rounded-lg border border-ink-300 bg-white"
            />
          ))}
        </ul>
      ) : categories.data.data.length === 0 ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <EmptyState
            icon={Tags}
            title={search ? 'No categories match that search.' : 'No property categories yet.'}
            hint={
              search
                ? 'Try a shorter search, or clear it to see them all.'
                : 'A category is a type of property — a house, an office, a rental — and it carries the checklist a cleaner works through.'
            }
            action={
              search ? null : (
                <Button
                  onClick={() => {
                    setCreating(true);
                  }}
                >
                  <Plus aria-hidden="true" />
                  Add category
                </Button>
              )
            }
          />
        </div>
      ) : (
        <>
          <ul className="space-y-3">
            {categories.data.data.map((category) => (
              <CategoryCard key={category.id} category={category} />
            ))}
          </ul>

          <Pagination
            page={categories.data.page}
            pageSize={categories.data.pageSize || DEFAULT_PAGE_SIZE}
            total={categories.data.total}
            onPageChange={setPage}
            itemNoun="categories"
          />
        </>
      )}

      <CategoryFormDialog open={creating} onOpenChange={setCreating} />
    </AdminPage>
  );
}

function CategoryCard({ category }: { category: PropertyCategoryView }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);

  return (
    <li className="overflow-hidden rounded-lg border border-ink-300 bg-white shadow-card">
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-2">
          {/*
            The disclosure is the whole left end of the row rather than the
            chevron alone — 44px minimum, and the name is the obvious thing to
            press. design-system.md §9.6.
          */}
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => {
              setExpanded((current) => !current);
            }}
            className="-m-2 flex min-h-touch min-w-0 items-start gap-2 p-2 text-left hover:text-bark-700"
          >
            {expanded ? (
              <ChevronDown aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-ink-500" />
            ) : (
              <ChevronRight aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-ink-500" />
            )}
            <span className="min-w-0">
              <span className="block text-h3 text-ink-900">{category.name}</span>
              <span className="mt-0.5 block text-meta text-ink-500">
                {/* Zero is `0`, never a dash — P1. */}
                {category.taskCount === 1 ? '1 task' : `${String(category.taskCount)} tasks`}
                {category.description ? ` · ${category.description}` : ''}
              </span>
            </span>
          </button>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {category.needsReview ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-status-pending/15 px-2.5 py-0.5 text-meta font-medium text-status-pending-text">
              <TriangleAlert aria-hidden="true" className="size-3.5" />
              Needs review
            </span>
          ) : null}
          {category.isActive ? null : <StatusBadge label="Inactive" tone="cancelled" />}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setEditing(true);
            }}
          >
            Edit
          </Button>
        </div>
      </div>

      {category.needsReview && category.reviewNote ? (
        <p className="border-t border-ink-300 bg-honey-50 px-4 py-2.5 text-meta text-bark-900">
          {category.reviewNote}
        </p>
      ) : null}

      {expanded ? (
        <div className="border-t border-ink-300 bg-ink-100/40 p-4">
          <ChecklistEditor categoryId={category.id} categoryName={category.name} />
        </div>
      ) : null}

      <CategoryFormDialog open={editing} onOpenChange={setEditing} category={category} />
    </li>
  );
}
